/**
 * Handing a key to the server, and finding out whether it was right.
 *
 * The check does not happen here. The app is a static bundle; anything it
 * compares is visible to whoever it is meant to keep out. What happens here is
 * the other half: put the key where nginx reads it, ask the server for the
 * protected path, and let its answer decide. A wrong key never gets the
 * bundle, only a refusal.
 */

import { FULL_APP_PATH, KEY_COOKIE, KEY_MAX_AGE } from '../../config'

/** How an unlock attempt ended. */
export type UnlockResult = 'ok' | 'rejected' | 'unreachable'

/** Store the key where the server will look for it. */
export function rememberKey(key: string, target: Document = document): void {
  const secure = target.location.protocol === 'https:' ? '; Secure' : ''
  target.cookie =
    `${KEY_COOKIE}=${encodeURIComponent(key)}; path=/; max-age=${String(KEY_MAX_AGE)}` +
    `; SameSite=Lax${secure}`
}

/** Forget it again, e.g. after the server refused it. */
export function forgetKey(target: Document = document): void {
  target.cookie = `${KEY_COOKIE}=; path=/; max-age=0; SameSite=Lax`
}

/**
 * Try a key against the server.
 *
 * Asks for the protected path rather than for a verdict: there is no endpoint
 * that says yes or no, only a path that either exists for you or does not.
 *
 * @param key - What the user typed.
 * @param fetcher - Injected in tests.
 */
export async function tryKey(
  key: string,
  fetcher: typeof fetch = fetch,
  target: Document = document,
): Promise<UnlockResult> {
  const trimmed = key.trim()
  if (trimmed === '') return 'rejected'

  rememberKey(trimmed, target)
  try {
    const response = await fetcher(FULL_APP_PATH, {
      method: 'HEAD',
      // The cookie is the whole point, and the path is same-origin.
      credentials: 'same-origin',
      cache: 'no-store',
    })
    if (response.ok) return 'ok'
    forgetKey(target)
    return 'rejected'
  } catch {
    // Offline, or the server is down. Not the same as a wrong key, and
    // saying so saves the user from retyping a key that was fine.
    forgetKey(target)
    return 'unreachable'
  }
}
